import React, { useState } from 'react';
import { Phone, Flag, Wrench, Settings, Hammer, Scissors, Star, CheckCircle, ArrowRight, MapPin } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { companyInfo, services, installationTypes, testimonials, galleryImages, aboutContent } from '../mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const iconMap = {
    wrench: Wrench,
    flag: Flag,
    settings: Settings,
    hammer: Hammer,
    scissors: Scissors
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service_type: formData.serviceType,
        message: formData.message
      });

      toast({
        title: "Inquiry Sent!",
        description: response.data.message || "Thank you for contacting us. We'll get back to you soon.",
      });
      
      setFormData({ name: '', email: '', phone: '', serviceType: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Error",
        description: "Failed to send inquiry. Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-red-50 text-red-700 px-4 py-2 rounded-full mb-6">
              <Flag className="w-4 h-4" />
              <span className="text-sm font-semibold">Professional Flagpole Services</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-blue-950 mb-6 leading-tight">
              Proudly Serving America with
              <span className="text-red-600"> Quality Flagpole</span> Solutions
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Expert installation, repair, and maintenance services for residential, commercial, and custom flagpoles. We install flagpoles of all sizes — including large commercial flagpoles over 100 feet tall. Based in Dallas, Texas, serving the entire nation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button onClick={scrollToContact} size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                Get Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <a href={`tel:${companyInfo.phoneRaw}`}>
                <Button size="lg" variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-50 px-8 py-6 text-lg">
                  <Phone className="mr-2 w-5 h-5" />
                  {companyInfo.phone}
                </Button>
              </a>
            </div>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Nationwide Service</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-blue-950 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive flagpole solutions tailored to your needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-2 hover:border-red-600 transition-all hover:shadow-xl group">
                  <CardHeader>
                    <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                      <IconComponent className="w-7 h-7 text-blue-900 group-hover:text-white transition-colors" />
                    </div>
                    <CardTitle className="text-xl text-blue-950">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Installation Types Section */}
      <section id="installations" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-blue-950 mb-4">Installation Options</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Professional flagpole installations for every need
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {installationTypes.map((type) => (
              <Card key={type.id} className="border-2 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <CardTitle className="text-2xl text-blue-950">{type.title}</CardTitle>
                  <CardDescription className="text-gray-600 mt-2">
                    {type.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {type.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button onClick={scrollToContact} className="w-full mt-6 bg-blue-900 hover:bg-blue-800 text-white">
                    Request Quote
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-blue-950 mb-6">{aboutContent.title}</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {aboutContent.description}
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {aboutContent.mission}
              </p>
              <div className="space-y-4">
                {aboutContent.values.map((value, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <Star className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-700 text-lg">{value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-red-50 rounded-2xl p-8 h-96 flex items-center justify-center">
              <div className="w-full h-full flex items-center justify-center">
                <img 
                  src="/logo-flagpoles.png" 
                  alt="Eagle Angel Flags - Honoring America" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-blue-950 mb-4">Our Work</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A showcase of our professional installations and services
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image) => (
              <Card key={image.id} className="overflow-hidden hover:shadow-xl transition-shadow group">
                <div className="h-64 bg-gradient-to-br from-blue-200 to-red-100 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                  <Flag className="w-20 h-20 text-blue-900 opacity-50" />
                </div>
                <CardHeader>
                  <CardTitle className="text-lg text-blue-950">{image.title}</CardTitle>
                  <CardDescription>{image.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* Testimonials Section */}
      <section className="testimonials-section py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-blue-950 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Trusted by homeowners and businesses across the nation
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2">
                <CardHeader>
                  <div className="flex items-center space-x-1 mb-2">
                    {[...Array(testimonial.rating)].map((_, idx) => (
                      <Star key={idx} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <CardTitle className="text-lg text-blue-950">{testimonial.name}</CardTitle>
                  <CardDescription className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span>{testimonial.location}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 italic leading-relaxed">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-blue-950 mb-4">Contact Us</h2>
            <p className="text-xl text-gray-600">
              Ready to get started? Fill out the form below or give us a call
            </p>
          </div>
          <Card className="border-2 shadow-xl">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                      className="border-gray-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(123) 456-7890"
                      required
                      className="border-gray-300"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    required
                    className="border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="serviceType">Service Type *</Label>
                  <Select
                    value={formData.serviceType}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, serviceType: value }))}
                    required
                  >
                    <SelectTrigger className="border-gray-300">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="repair">Flagpole Repair</SelectItem>
                      <SelectItem value="replacement">Flag Replacement</SelectItem>
                      <SelectItem value="maintenance">Maintenance Services</SelectItem>
                      <SelectItem value="installation-residential">Residential Installation</SelectItem>
                      <SelectItem value="installation-commercial">Commercial Installation</SelectItem>
                      <SelectItem value="installation-custom">Custom Installation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your project..."
                    rows={5}
                    required
                    className="border-gray-300"
                  />
                </div>
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
              <div className="mt-8 pt-8 border-t border-gray-200 text-center">
                <p className="text-gray-600 mb-4">Or reach us directly:</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a href={`tel:${companyInfo.phoneRaw}`} className="flex items-center space-x-2 text-blue-900 hover:text-red-600 transition-colors font-medium">
                    <Phone className="w-5 h-5" />
                    <span>{companyInfo.phone}</span>
                  </a>
                  <span className="hidden sm:inline text-gray-400">|</span>
                  <a href={`mailto:${companyInfo.email}`} className="flex items-center space-x-2 text-blue-900 hover:text-red-600 transition-colors font-medium">
                    <span>{companyInfo.email}</span>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;